import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Alert
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import QrCodeIcon from '@mui/icons-material/QrCode';
import moment from 'moment';

const VisitorAccess = () => {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [visitorPurpose, setVisitorPurpose] = useState('');
  const [accessDate, setAccessDate] = useState('');
  const [error, setError] = useState('');
  
  const { user } = useSelector(state => state.auth);
  
  // 載入訪客數據
  useEffect(() => {
    // 模擬API載入
    setTimeout(() => {
      const mockVisitors = [
        {
          id: 'v1',
          name: '李小明',
          phone: '9123 4567',
          purpose: '送貨',
          date: '2023-05-25',
          time: '14:00-16:00',
          status: 'active',
          code: 'ABC123',
          createdAt: '2023-05-24T10:30:00'
        },
        {
          id: 'v2',
          name: '張三',
          phone: '9876 5432',
          purpose: '維修',
          date: '2023-05-26',
          time: '10:00-12:00',
          status: 'pending',
          code: 'DEF456',
          createdAt: '2023-05-24T11:45:00'
        },
        {
          id: 'v3',
          name: '王小紅',
          phone: '9345 6789',
          purpose: '探訪',
          date: '2023-05-20',
          time: '17:00-19:00',
          status: 'expired',
          code: 'GHI789',
          createdAt: '2023-05-19T08:15:00'
        }
      ];
      
      setVisitors(mockVisitors);
      setLoading(false);
    }, 1000);
  }, []);
  
  // 返回訪客狀態標籤
  const getStatusChip = (status) => {
    if (status === 'active') {
      return <Chip label="有效" color="success" size="small" />;
    } else if (status === 'pending') {
      return <Chip label="等待中" color="primary" size="small" />;
    } else if (status === 'expired') {
      return <Chip label="已過期" color="error" size="small" />;
    } else if (status === 'used') {
      return <Chip label="已使用" color="default" size="small" />;
    }
  };
  
  // 檢查有效期
  const isValidDate = (date) => {
    return moment(date, 'YYYY-MM-DD').isSameOrAfter(moment(), 'day');
  };
  
  // 處理新增訪客提交
  const handleAddVisitor = () => {
    // 驗證表單
    if (!visitorName || !visitorPhone || !visitorPurpose || !accessDate) {
      setError('請填寫所有必填字段');
      return;
    }
    
    if (!isValidDate(accessDate)) {
      setError('日期必須是今天或將來的日期');
      return;
    }
    
    // 模擬添加處理
    const newVisitor = {
      id: `v${visitors.length + 1}`,
      name: visitorName,
      phone: visitorPhone,
      purpose: visitorPurpose,
      date: accessDate,
      time: '09:00-18:00',
      status: 'pending',
      code: Math.random().toString(36).substring(2, 8).toUpperCase(),
      createdAt: new Date().toISOString()
    };
    
    setVisitors([newVisitor, ...visitors]);
    setShowAddDialog(false);
    resetForm();
  };
  
  // 處理刪除訪客
  const handleDeleteVisitor = (id) => {
    setVisitors(visitors.filter(visitor => visitor.id !== id));
  };
  
  // 查看二維碼
  const handleViewQRCode = (visitor) => {
    setSelectedVisitor(visitor);
    setShowQRCode(true);
  };
  
  // 重置表單
  const resetForm = () => {
    setVisitorName('');
    setVisitorPhone('');
    setVisitorPurpose('');
    setAccessDate('');
    setError('');
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">訪客通行管理</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setShowAddDialog(true)}
        >
          新增訪客通行
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visitors.length > 0 ? (
        <Paper>
          <List>
            {visitors.map((visitor) => (
              <ListItem key={visitor.id} divider>
                <ListItemIcon>
                  <Avatar>
                    <PersonIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={visitor.name}
                  secondary={
                    <React.Fragment>
                      <Typography variant="body2" component="span" color="text.primary">
                        {visitor.purpose}
                      </Typography>
                      <br />
                      <Typography variant="body2" component="span">
                        <EventIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {visitor.date}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                        <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {visitor.time}
                      </Typography>
                    </React.Fragment>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getStatusChip(visitor.status)}
                  {visitor.status === 'active' && (
                    <IconButton 
                      color="primary" 
                      sx={{ ml: 1 }}
                      onClick={() => handleViewQRCode(visitor)}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  )}
                  <IconButton 
                    color="error" 
                    sx={{ ml: 1 }}
                    onClick={() => handleDeleteVisitor(visitor.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            沒有訪客通行記錄
          </Typography>
        </Paper>
      )}
      
      {/* 添加訪客對話框 */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新增訪客通行</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="訪客姓名"
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="聯絡電話"
                value={visitorPhone}
                onChange={(e) => setVisitorPhone(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="來訪目的"
                value={visitorPurpose}
                onChange={(e) => setVisitorPurpose(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="訪問日期"
                type="date"
                value={accessDate}
                onChange={(e) => setAccessDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>取消</Button>
          <Button 
            variant="contained" 
            onClick={handleAddVisitor}
            startIcon={<HowToRegIcon />}
          >
            建立通行證
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 二維碼對話框 */}
      <Dialog open={showQRCode} onClose={() => setShowQRCode(false)} maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {selectedVisitor && (
            <>
              <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', display: 'inline-block' }}>
                <Box sx={{ 
                  width: 200, 
                  height: 200, 
                  bgcolor: '#f5f5f5', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <QrCodeIcon sx={{ fontSize: 100, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    模擬二維碼
                  </Typography>
                </Box>
              </Box>
              <Typography variant="h6">
                訪客通行碼: {selectedVisitor.code}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                訪客: {selectedVisitor.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                訪問日期: {selectedVisitor.date}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                有效時間: {selectedVisitor.time}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQRCode(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitorAccess; 